import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";
import type { CategoricalChartFunc } from "recharts/types/chart/types";
import { useTranslation } from "react-i18next";
import type { LabReading, ChartConfig } from "../types";
import { DOT_COLORS, CHART_COLORS } from "../constants";
import { getBarColor } from "../utils";

interface CustomBarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: LabReading & { isSelected?: boolean };
}

const CustomBarShape = (props: CustomBarShapeProps) => {
  const { x = 0, y = 0, width = 0, height = 0, fill, payload } = props;
  const isSelected = payload?.isSelected;
  const extra = isSelected ? 8 : 0;
  return <rect x={x - extra / 2} y={y} width={width + extra} height={Math.max(0, height)} fill={fill} rx={4} ry={4} />;
};

interface CustomDotProps {
  cx?: number;
  cy?: number;
  payload?: LabReading & { isSelected?: boolean };
}

const CustomDot = ({ cx = 0, cy = 0, payload }: CustomDotProps) => {
  const color = DOT_COLORS[payload?.statusIndicator ?? "default"];
  const isSelected = payload?.isSelected;
  return <circle cx={cx} cy={cy} r={isSelected ? 7 : 4} fill={color} stroke="white" strokeWidth={2} />;
};

export interface ChartRendererProps {
  config: ChartConfig;
  resultName: string;
  resultUnit: string;
  onDataPointClick?: CategoricalChartFunc;
}

export const ChartRenderer = ({ config, resultName, resultUnit, onDataPointClick }: ChartRendererProps) => {
  const { t } = useTranslation("ui");
  const { type, filteredData, refRange, yMin, yMax, hasData } = config;

  if (!hasData) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
        {t("No data for this period")}
      </div>
    );
  }

  const commonProps = {
    data: filteredData,
    margin: { top: 8, right: 16, left: -8, bottom: 0 },
    onClick: onDataPointClick,
  };

  return (
    <div data-slot="chart-renderer" className="h-48">
      {type === "line" ? (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <YAxis
              domain={[yMin, yMax]}
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              contentStyle={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                fontSize: 12,
                padding: "6px 10px",
                boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
              }}
              formatter={(val) => [`${val} ${resultUnit}`, resultName]}
              labelStyle={{ color: "#6b7280", marginBottom: 2 }}
            />
            {refRange && (
              <>
                <ReferenceLine y={refRange.min} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.6} />
                <ReferenceLine y={refRange.max} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.6} />
              </>
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={CHART_COLORS.line.stroke}
              strokeWidth={2}
              dot={(props) => <CustomDot {...props} />}
              activeDot={{ r: 6, fill: CHART_COLORS.line.activeDot, stroke: "white", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart {...commonProps} barSize={24}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <YAxis
              domain={[yMin, yMax]}
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
              contentStyle={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                fontSize: 12,
                padding: "6px 10px",
                boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
              }}
              formatter={(val) => [`${val} ${resultUnit}`, resultName]}
              labelStyle={{ color: "#6b7280", marginBottom: 2 }}
            />
            {refRange && (
              <>
                <ReferenceLine y={refRange.min} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.6} />
                <ReferenceLine y={refRange.max} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.6} />
              </>
            )}
            <Bar dataKey="value" shape={<CustomBarShape />}>
              {filteredData.map((entry) => (
                <Cell key={entry.id} fill={getBarColor(entry.statusIndicator)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
